import { SupportChannelSelection } from "@/components/internal-chats/support-channel-list";
import { SupportChatMuteBar } from "@/components/internal-chats/support-chat-mute-bar";
import { Card } from "@/components/ui/card";
import { useCurrentUser } from "@/lib/api/user";
import { X } from "lucide-react";
import { useState } from "react";
import type { GroupChannel } from "@sendbird/chat/groupChannel";

import { ChatDetail } from "./chat-detail.tsx";
import { ChatList } from "./chat-list.tsx";
import { cn } from "@/lib/utils.ts";

interface ChatPopupProps {
  channels: GroupChannel[];
  channelsLoading: boolean;
  onClose: () => void;
  isOpen: boolean;
  position: "left" | "right";
  isEscalationTriggerVisible: boolean;
  /** When true, parent anchors the popup; skip FAB absolute offsets. */
  anchored?: boolean;
}

export const ChatPopup = ({
  channels,
  channelsLoading,
  onClose,
  isOpen,
  position,
  isEscalationTriggerVisible,
  anchored = false,
}: ChatPopupProps) => {
  const [selection, setSelection] = useState<SupportChannelSelection | null>(
    null,
  );
  const { data: currentUser } = useCurrentUser();

  return (
    <Card
      className={cn(
        "popup-chat w-[500px] shadow-lg",
        anchored
          ? "relative"
          : cn(
              "absolute bottom-16",
              position === "left" ? "left-0" : "right-0",
              isEscalationTriggerVisible && "right-2",
            ),
        !isOpen && "hidden",
      )}
    >
      {!selection && (
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold">Chats</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      <div
        className={cn(
          "overflow-y-auto",
          !selection && "h-[640px]",
          selection && "h-[701px]",
        )}
      >
        {selection ? (
          <ChatDetail
            currentChannelUrl={selection.sdkUrl}
            internalChannelId={selection.internalChannelId}
            onBack={() => setSelection(null)}
            onClose={onClose}
          />
        ) : (
          <ChatList
            channels={channels}
            loading={channelsLoading}
            currentChannelUrl={null}
            onSelectChat={setSelection}
          />
        )}
      </div>
      {!selection && <SupportChatMuteBar userId={currentUser?.id ?? null} />}
    </Card>
  );
};
